import React, { useRef, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTime } from '../../utils/timeUtils';
import { getAnnotationColor } from '../../utils/annotationUtils';
import { PLAYER_COLORS, TIMELINE_VIEWPORT_WIDTH, ZOOM_LEVELS, TIME_WINDOWS, getFPS } from '../../utils/constants';
import { getFrameForTime } from '../../utils/frameIndex';
import Button from '../ui/Button';
import { PlayIcon, PauseIcon } from '../ui/Icons';

const TimelineContainer = styled.div`
  margin-top: ${theme.spacing.xl};
  width: ${TIMELINE_VIEWPORT_WIDTH}px;
  background-color: transparent;
  border-radius: ${theme.borderRadius.lg};
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  margin-right: 0;
  flex-shrink: 0;
  margin-bottom: 0px;
  
  /* Responsive width for smaller screens */
  @media screen and (max-width: 1440px) {
    width: 960px;
  }
`;

const TimelineRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

const TimelineScroll = styled.div`
  width: calc(100% - 170px);
  margin-left: 50px;
  height: 40px;
  overflow-x: ${props => props.isFullTimeline ? 'hidden' : 'auto'};
  overflow-y: hidden;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.xl};
  position: relative;
  white-space: nowrap;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.primary} ${theme.colors.background};
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.background};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.primaryHover};
  }
`;

const TimelineBar = styled.div`
  width: ${props => props.width}px;
  height: 100%;
  position: relative;
  background-color: ${props => props.zoomed ? theme.colors.surface : theme.colors.background};
  border-radius: ${theme.borderRadius.xl};
  cursor: pointer;
`;

const TIMELINE_TRACK_TOP = 4;
const TIMELINE_TRACK_HEIGHT = 32;

const ProgressBar = styled.div`
  width: ${props => props.progress}%;
  height: ${TIMELINE_TRACK_HEIGHT}px;
  background-color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.xl};
  transition: width 0.1s ease;
  position: absolute;
  left: 0;
  top: ${TIMELINE_TRACK_TOP}px;
  z-index: 1;
`;

const AnnotationMarker = styled.div`
  position: absolute;
  left: ${props => props.left}px;
  top: ${TIMELINE_TRACK_TOP}px;
  width: 4px;
  height: ${TIMELINE_TRACK_HEIGHT}px;
  background-color: ${props => props.color};
  border-radius: 2px;
  cursor: pointer;
  transform: translateX(-50%);
  z-index: 2;
`;

const CurrentTimeIndicator = styled.div`
  position: absolute;
  left: ${props => props.left}px;
  top: 0;
  width: 2px;
  height: 100%;
  background-color: ${theme.colors.text};
  transform: translateX(-50%);
  z-index: 4;
`;

const TimeLabels = styled.div`
  position: absolute;
  right: 0px;
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.text};
  min-width: 120px;
  text-align: right;
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.normal};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const TimeLabel = styled.span`
  font-size: ${theme.typography.fontSizes.xs};
  color: ${theme.colors.text};
  font-weight: ${theme.typography.fontWeights.normal};
`;

const ZoomInfo = styled.span`
  font-size: ${theme.typography.fontSizes.xs};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.fontWeights.normal};
`;




/**
 * Static markers that only re-render when annotations/zoom change, NOT on
 * every currentTime tick.  This is the O(n) part of the timeline — isolating
 * it behind React.memo keeps the per-frame render cost O(1).
 */
const rallyTitle = (rally, index, score) => {
  const header = `Rally ${index + 1}: ${formatTime(rally.startTime)} – ${formatTime(rally.endTime)}`;
  if (!score || score.pointScore === null) return `${header}\nScore unknown`;
  const points = score.isTiebreak ? `Tiebreak ${score.pointScore}` : score.pointScore;
  return `${header}\n${points}  ·  Games ${score.gameScore}  ·  Sets ${score.setScore}`;
};

const TimelineMarkers = React.memo(({
  timelineAnnotations,
  pxPerSecond,
  timelineBarWidth,
  isBallMode,
  isRallyMode,
  rallies,
  scoreById,
  pendingStart,
  playerRef,
}) => (
  <>
    {/* Scored rally spans, tinted by winner. Only in rally mode, where they
        are the thing being edited — elsewhere they would just be clutter. */}
    {isRallyMode && rallies.map((rally, index) => (
      <div
        key={rally.id}
        title={rallyTitle(rally, index, scoreById.get(rally.id))}
        style={{
          position: 'absolute',
          left: `${rally.startTime * pxPerSecond}px`,
          width: `${Math.max(1, (rally.endTime - rally.startTime) * pxPerSecond)}px`,
          top: 0,
          height: '100%',
          background: rally.winner === 'near' ? `${PLAYER_COLORS.green}CC`
                    : rally.winner === 'far'  ? `${PLAYER_COLORS.red}CC`
                    : 'rgba(255, 255, 255, 0.5)',
          borderLeft: `1px solid ${theme.colors.accent}`,
          borderRight: `1px solid ${theme.colors.accent}`,
          boxSizing: 'border-box',
          cursor: 'pointer',
          zIndex: 3,
        }}
      />
    ))}

    {/* Rally in progress: open-ended, so its start is visible while scrubbing */}
    {isRallyMode && pendingStart !== null && (
      <div
        style={{
          position: 'absolute',
          left: `${pendingStart * pxPerSecond}px`,
          width: `${Math.max(0, timelineBarWidth - pendingStart * pxPerSecond)}px`,
          top: 0,
          height: '100%',
          background: `${theme.colors.accent}88`,
          borderLeft: `2px solid ${theme.colors.accent}`,
          boxSizing: 'border-box',
          pointerEvents: 'none',
          zIndex: 3,
        }}
      />
    )}

    {/* Annotation Markers */}
    {isBallMode && timelineAnnotations.map(annotation => {
      const markerPosition = annotation.timestamp * pxPerSecond;
      return (
        <AnnotationMarker
          key={annotation.id}
          left={markerPosition}
          color={getAnnotationColor(annotation.type)}
          title={`${annotation.type.charAt(0).toUpperCase() + annotation.type.slice(1)} at ${formatTime(annotation.timestamp)}`}
          onClick={(e) => {
            e.stopPropagation();
            if (playerRef.current) {
              playerRef.current.currentTime(annotation.timestamp);
              setTimeout(() => {
                if (playerRef.current) {
                  playerRef.current.pause();
                  playerRef.current.currentTime(annotation.timestamp);
                }
              }, 10);
            }
          }}
        />
      );
    })}

    {/* Court keypoint markers intentionally omitted from the timeline.
        Court annotations can exist on every frame (reprojected or primary),
        which would create thousands of DOM nodes and cause lag — the same
        reason ball in-flight ticks are excluded. */}
  </>
));

const Timeline = ({
  currentTime,
  videoDuration,
  annotations,
  playerRef,
  isPaused,
  isBallMode,
  isRallyMode,
  rallies = [],
  scoreboard = [],
  pendingStart = null,
  timelineZoom,
  setTimelineZoom,
  onPlayPause,
}) => {
  const [timelineZoomCenter, setTimelineZoomCenter] = useState(0);
  const scoreById = useMemo(
    () => new Map(scoreboard.map((entry) => [entry.id, entry])),
    [scoreboard]
  );
  const [timelineScroll, setTimelineScroll] = useState(0);
  const timelineScrollRef = useRef(null);

  // Measure container width once when zoom/duration changes, then keep it
  // stable so TimelineMarkers' React.memo isn't busted by DOM-read jitter.
  const [containerWidth, setContainerWidth] = useState(TIMELINE_VIEWPORT_WIDTH - 170);
  useEffect(() => {
    if (timelineScrollRef.current) {
      setContainerWidth(timelineScrollRef.current.clientWidth);
    }
  }, [timelineZoom, videoDuration]);

  const getTimeWindow = (zoom) => {
    const index = ZOOM_LEVELS.findIndex(level => level === zoom || (level !== null && Math.abs(level - zoom) < 0.01));
    if (index >= 0) {
      const timeWindow = TIME_WINDOWS[index];
      return timeWindow === null ? videoDuration : timeWindow;
    }
    return 15;
  };

  const visibleTimeWindow = getTimeWindow(timelineZoom);

  // Memoize layout values so TimelineMarkers' React.memo sees stable props
  const { timelineBarWidth, pxPerSecond } = useMemo(() => {
    if (videoDuration <= 0) return { timelineBarWidth: TIMELINE_VIEWPORT_WIDTH, pxPerSecond: 0 };
    if (timelineZoom === null) {
      return { timelineBarWidth: containerWidth, pxPerSecond: containerWidth / videoDuration };
    }
    const w = (videoDuration / visibleTimeWindow) * TIMELINE_VIEWPORT_WIDTH;
    return { timelineBarWidth: w, pxPerSecond: w / videoDuration };
  }, [videoDuration, timelineZoom, visibleTimeWindow, containerWidth]);

  // Only show strokes, bounces, and net hits on the timeline (skip in-flight).
  // In-flight annotations are the most numerous (one per frame between events)
  // and would create thousands of DOM nodes, causing lag.
  const timelineAnnotations = useMemo(() => {
    if (!annotations || annotations.length === 0) return [];
    return annotations.filter(ann => ann.type === 'stroke' || ann.type === 'bounce' || ann.type === 'net');
  }, [annotations]);

  // Calculate frame number from current time using frame index (falls back to FPS)
  const currentFrame = getFrameForTime(currentTime);
  
  // Format frame number with commas for readability
  const formatFrameNumber = (frame) => {
    return frame.toLocaleString();
  };

  // Calculate the visible time range based on scroll position
  const getVisibleTimeRange = () => {
    if (!timelineScrollRef.current || !videoDuration) return { start: 0, end: videoDuration };
    
    const scrollLeft = timelineScrollRef.current.scrollLeft;
    const viewportWidth = timelineScrollRef.current.clientWidth;
    
    // Calculate the time range visible in the viewport
    const startTime = (scrollLeft / timelineBarWidth) * videoDuration;
    const endTime = ((scrollLeft + viewportWidth) / timelineBarWidth) * videoDuration;
    
    return {
      start: Math.max(0, startTime),
      end: Math.min(videoDuration, endTime)
    };
  };

  // Sync scroll position when timelineScroll changes
  useEffect(() => {
    if (!timelineScrollRef.current || timelineZoom === null) return;
    const maxScroll = timelineBarWidth - TIMELINE_VIEWPORT_WIDTH;
    timelineScrollRef.current.scrollLeft = timelineScroll * maxScroll;
  }, [timelineScroll, timelineBarWidth, timelineZoom]);

  // Sync timelineScroll state when user scrolls timeline
  const handleTimelineScroll = () => {
    if (!timelineScrollRef.current || timelineZoom === null) return;
    const maxScroll = timelineBarWidth - TIMELINE_VIEWPORT_WIDTH;
    setTimelineScroll(timelineScrollRef.current.scrollLeft / maxScroll);
  };

  // On playhead move, advance to next window when progress bar reaches right edge
  useEffect(() => {
    if (!timelineScrollRef.current || !videoDuration || timelineZoom === null) return;
    
    const playheadPx = currentTime * pxPerSecond;
    const scrollLeft = timelineScrollRef.current.scrollLeft;
    const viewportWidth = timelineScrollRef.current.clientWidth;
    
    // Check if playhead has reached the right edge of the visible window
    if (playheadPx > scrollLeft + viewportWidth - 40) {
      // Advance to next window by scrolling right
      const newScrollLeft = scrollLeft + viewportWidth;
      const maxScroll = Math.max(0, timelineBarWidth - viewportWidth);
      
      if (newScrollLeft <= maxScroll) {
        timelineScrollRef.current.scrollLeft = newScrollLeft;
        setTimelineScroll(newScrollLeft / maxScroll);
      }
    }
  }, [currentTime, pxPerSecond, videoDuration, timelineBarWidth, timelineZoom]);

  // Reset timeline zoom when video changes
  useEffect(() => {
    setTimelineZoom(1);
    setTimelineZoomCenter(0);
  }, [videoDuration]);

  // Handle zoom changes - keep current scroll position relative to timeline
  useEffect(() => {
    if (!timelineScrollRef.current || !videoDuration) return;
    
    // Calculate the new timeline width based on zoom
    const newTimeWindow = getTimeWindow(timelineZoom);
    const actualTimeWindow = newTimeWindow === null ? videoDuration : newTimeWindow;
    const newTimelineBarWidth = videoDuration > 0 ? (videoDuration / actualTimeWindow) * TIMELINE_VIEWPORT_WIDTH : TIMELINE_VIEWPORT_WIDTH;
    
    // Maintain the same relative position in the timeline when zooming
    const currentScrollRatio = timelineScrollRef.current.scrollLeft / (timelineBarWidth - (TIMELINE_VIEWPORT_WIDTH - 170));
    const newMaxScroll = Math.max(0, newTimelineBarWidth - (TIMELINE_VIEWPORT_WIDTH - 170));
    const newScrollLeft = currentScrollRatio * newMaxScroll;
    
    timelineScrollRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, newMaxScroll));
  }, [timelineZoom, videoDuration, timelineBarWidth]);

  // Handle timeline click
  const handleTimelineClick = (event) => {
    if (!playerRef.current || !videoDuration) return;
    
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    
    // Get the current scroll position
    const currentScrollLeft = timelineScrollRef.current?.scrollLeft || 0;
    
    // Calculate the absolute position of the click on the timeline
    const absoluteClickX = clickX + currentScrollLeft;
    
    // Convert the absolute position to time
    const clickedTime = (absoluteClickX / pxPerSecond);
    
    // Clamp the time to valid video duration
    const clampedTime = Math.max(0, Math.min(clickedTime, videoDuration));
    
    
    // Jump to the clicked time
    playerRef.current.currentTime(clampedTime);
  };





  return (
    <TimelineContainer>
      <TimelineRow>
        <Button
          variant="play"
          onClick={onPlayPause}
        >
          {playerRef.current && playerRef.current.paused() ? <PlayIcon /> : <PauseIcon />}
        </Button>

        <TimelineScroll
          ref={timelineScrollRef}
          onScroll={handleTimelineScroll}
          isFullTimeline={timelineZoom === null}
          onClick={handleTimelineClick}
        >
          <TimelineBar
            width={timelineBarWidth}
            zoomed={timelineZoom !== 1}
          >
            <ProgressBar progress={(currentTime / videoDuration) * 100} />
            
            <TimelineMarkers
              timelineAnnotations={timelineAnnotations}
              pxPerSecond={pxPerSecond}
              timelineBarWidth={timelineBarWidth}
              isBallMode={isBallMode}
              isRallyMode={isRallyMode}
              rallies={rallies}
              scoreById={scoreById}
              pendingStart={pendingStart}
              playerRef={playerRef}
            />

            {/* Current Time Indicator */}
            <CurrentTimeIndicator left={currentTime * pxPerSecond} />
          </TimelineBar>
        </TimelineScroll>
        
        <TimeLabels>
          <TimeLabel>
            {formatTime(currentTime)}
            <span>{` (Frame ${formatFrameNumber(currentFrame)})`}</span>
          </TimeLabel>

        </TimeLabels>
        </TimelineRow>
      </TimelineContainer>
    );
  };

export default React.memo(Timeline);